import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Banner que invita a usar el módulo "Mi ciclo hormonal".
// Aparece solo si banner_status === 'unseen'. Permite explorar, omitir, u ocultar permanentemente.
export default function HormonalCycleBanner() {
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(true);

  const authHeaders = () => {
    const token = localStorage.getItem('deisycfit_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    const check = async () => {
      try {
        const res = await axios.get(`${API}/hormonal-cycle/status`, { headers: authHeaders() });
        setShow(res.data?.banner_status === 'unseen');
      } catch (e) { /* silent */ } finally {
        setLoading(false);
      }
    };
    check();
  }, []);

  const updateStatus = async (status) => {
    try {
      await axios.put(`${API}/hormonal-cycle/banner-status`, { status }, { headers: authHeaders() });
    } catch (e) { /* silent */ }
  };

  const handleExplore = async () => {
    await updateStatus('explored');
    navigate('/mi-ciclo-hormonal');
  };

  const handleDismiss = async () => {
    await updateStatus('dismissed');
    setShow(false);
  };

  const handleHidePermanent = async () => {
    await updateStatus('hidden_permanently');
    setShow(false);
  };

  if (loading || !show) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className="rounded-2xl p-5 md:p-6 mb-4 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, rgba(var(--brand-primary-glow), 0.15), rgba(192, 132, 252, 0.08))',
          border: '1px solid rgba(192, 132, 252, 0.3)'
        }}
        data-testid="hormonal-cycle-banner"
      >
        <div className="flex items-start gap-4">
          <div className="text-4xl flex-shrink-0">🌙</div>
          <div className="flex-1">
            <h3 className="text-white font-bold text-base md:text-lg mb-1" style={{ fontFamily: 'Poppins, sans-serif' }}>
              ✨ Nueva herramienta disponible: Mi ciclo hormonal
            </h3>
            <p className="text-gray-300 text-sm leading-relaxed mb-4">
              Registra tu ciclo y recibe sugerencias opcionales sobre energía, hambre, retención, recuperación y entrenamiento según cómo te sientas durante el mes.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button onClick={handleExplore} className="text-white text-sm" style={{ background: 'linear-gradient(135deg, var(--brand-primary), #C084FC)' }} data-testid="explore-cycle-btn">
                Explorar herramienta
              </Button>
              <Button variant="outline" size="sm" onClick={handleDismiss} className="border-white/20 text-gray-300 hover:bg-white/5" data-testid="dismiss-cycle-banner-btn">
                Omitir por ahora
              </Button>
              <Button variant="ghost" size="sm" onClick={handleHidePermanent} className="text-gray-500 hover:text-gray-300 text-xs" data-testid="hide-cycle-banner-btn">
                No volver a mostrar
              </Button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
